import React, { Suspense, useEffect } from 'react'
import tw, { styled } from 'twin.macro'
import { motion, AnimatePresence } from 'framer-motion'
import { useRouter } from 'next/router'
import { useAtomValue, useUpdateAtom } from 'jotai/utils'
import { Button } from '@chakra-ui/react'
import { Block } from 'baseui/block'

import { useNamedBoundingRectRef, useProgressControl } from '@/components/Tour'
import { useShowAccountSelectModal } from '@/components/SiteNav'
import { claimSpiritMachineAtom } from '@/features/spirit/atoms/claimSpiritMachineAtom'
import { CreateSpiritButton, UpdateSpiritButton, ClaimSpiritButton } from '@/features/spirit/components/buttons'

import { injectModalVisibleAtom } from './atoms/injectModalVisibleAtom'
import {  claimSpiritStageAnimateAtom, claimSpiritStageAtom, ClaimSpiritStageEnum } from './atoms/claimSpiritStageAtom'
import InsufficientBalanceModal from './components/InsufficientBalanceModal'
import ClaimSuccessModal from './components/ClaimSuccessModal'

const Wrapper = styled(motion.div)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  /* top: 67.51vh; */
  display: flex;
  flex-direction: column;
  align-items: center;
`

// Actions
const SpiritsConnected = styled(motion.div)`
  ${tw`font-secondary font-medium`}
  font-size: 1.25vw;
  color: #27ffe2;
  margin-top: 1.5vh;
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  top: 67.51vh;
`

const WarningBox = styled.div`
  margin-top: 4.04vh;
`

const WarningLable = styled.div`
  ${tw`font-primary font-bold`}
  font-size: 0.73vw;
  text-align: center;
  color: #ffffff;
  margin-bottom: 1.21vh;
`

const WarningText = styled.div`
  ${tw`font-primary`}
  font-size: 0.73vw;
  text-align: center;
  color: #b5b5b5;

  a {
    border-bottom: 1px solid #b5b5b5;
    margin: 0 0.15rem;
  }
`

const GoPreviewButton = () => {
  const router = useRouter()
  const ref = useNamedBoundingRectRef<HTMLButtonElement>('linkStartHint')
  const dispatch = useProgressControl('linkStartHint')
  useEffect(() => {
    const seed = setTimeout(() => dispatch('next'), 4000)
    return () => {
      if (seed) {
        clearTimeout(seed)
      }
      dispatch('cancel')
    }
  }, [dispatch])
  return (
    <Button
      ref={ref}
      onClick={() => {
        dispatch('cancel')
        router.push('/rarities')
      }}
    >
      Let&rsquo;s mint the Shell NFT
    </Button>
  )
}

const Tip = () => {
  const machine = useAtomValue(claimSpiritMachineAtom)
  const stage = useAtomValue(claimSpiritStageAtom)
  if (!machine.context.hasInited || stage === 'claimed') {
    return null
  }
  return (
    <WarningBox>
      <WarningLable>TIP</WarningLable>
      {stage === ClaimSpiritStageEnum.original && (
        <WarningText>
          Click on the flashing part to enter My Crypto Profile(decentralized
          Identity protocol) to generate your own on-chain identity. Specific
          behaviors will be converted into attribute and reputation stored in
          your Spirit.
        </WarningText>
      )}
      {(stage === ClaimSpiritStageEnum.injected && machine.can('claimSpirit')) && (
        <WarningText>
          Click button to enter the process of claiming Spirit!
        </WarningText>
      )}
      {(stage === ClaimSpiritStageEnum.injected && machine.can('updateSpirit') && !machine.can('claimSpirit') && !machine.context.spiritCollection?.length) && ( 
        <WarningText>
          Please click the UPDATE ATTRIBUTES button to inject your identity data into the Spirit.
        </WarningText>
      )}
      {(stage === ClaimSpiritStageEnum.injected && !machine.can('createSpirit') && !machine.context.spirit) && (
        <WarningText>
            Please keep at least 10 PHA in your account, or
            <a
              href="https://docs.phala.world/introduction/spirit-nft/attribute-logicenen"
              target="_blank"
              rel="noreferrer"
            >
              attribute above 0
            </a>
            , otherwise the claim will fail.
        </WarningText>
      )}
    </WarningBox>
  )
}

const ActionButton = () => {
  const machine = useAtomValue(claimSpiritMachineAtom)
  const showAccountSelectModal = useShowAccountSelectModal()
  const setInjectModalVisible = useUpdateAtom(injectModalVisibleAtom)

  const stage = useAtomValue(claimSpiritStageAtom)
  const variants = useAtomValue(claimSpiritStageAnimateAtom)

  const hasInited = machine.context.hasInited

  return (
    <>
      {(stage === ClaimSpiritStageEnum.claimed) && (
        <SpiritsConnected
          initial="initial"
          animate="animate"
          exit="exit"
          variants={variants.soulBound}
        >
          SOULBOUND 2.0
        </SpiritsConnected>
      )}
      <Wrapper
        initial="initial"
        animate="animate"
        exit="exit"
        variants={variants.bottomSection}
      >
        {machine.matches('anonymous') && (
          <Button onClick={showAccountSelectModal}>Connect Wallet</Button>
        )}
        {hasInited && stage === ClaimSpiritStageEnum.waiting && (
          <Button disabled>Preparing</Button>
        )}
        {(hasInited && !machine.matches('anonymous') && stage === ClaimSpiritStageEnum.original) && (
          <Button onClick={() => setInjectModalVisible(true)}>1/4 Generate identity</Button>
        )}
        {(hasInited && stage === ClaimSpiritStageEnum.injected) && (
          <>
            {/* create spirit */}
            {!machine.context.spirit && (
              <CreateSpiritButton>2/4 Kickstart the Claim Process</CreateSpiritButton>
            )}
            {/* update spirit */}
            {(!!machine.context.spirit && !(machine.context.spirit?.stats_refreshed_at) && !machine.context.spiritCollection?.length) && ( 
            // {machine.matches('signedIn.created') && (
              <Block display={'flex'} gridGap="1vw">
                <Suspense fallback={null}>
                  <Block display={'flex'} alignItems="center">
                    <UpdateSpiritButton>3/4 Update Attributes</UpdateSpiritButton>
                  </Block>
                </Suspense>
              </Block>
            )}
            {/* claim spirit */}
            {(machine.context.spirit && !!(machine.context.spirit?.stats_refreshed_at) && (machine.context.spiritCollection?.length === 0)) && (
              <Block display={'flex'} gridGap="1vw">
                <ClaimSpiritButton>4/4 CLAIM SPIRIT</ClaimSpiritButton>
              </Block>
            )}
          </>
        )}
        {(hasInited && stage === ClaimSpiritStageEnum.claimed) && (
          <GoPreviewButton />
        )}
        <Tip />
        <InsufficientBalanceModal />
        <ClaimSuccessModal />
      </Wrapper>
    </>
  )
}

const BottomActions: React.FC = () => {
  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={null}>
        <ActionButton />
      </Suspense>
    </AnimatePresence>
  )
}

export default BottomActions
